import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../common/Icon';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { AdjustmentsHorizontalIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import Layout from '../common/Layout';
import FilterPills from './FilterPills';
import ExpirationChips from './ExpirationChips';
import OptionsContractCard from './OptionsContractCard';
import OptionsVisualizationChart from './OptionsVisualizationChart';
import ContractComparison from './ContractComparison';
import MarketWeatherHeader from './MarketWeatherHeader';
import ContractLadder from './ContractLadder';
import ContractQuantityModal, { ConfirmedFills } from './ContractQuantityModal';
import ContractFilterSidebar, { ContractFilters } from './ContractFilterSidebar';
import { SavePresetModal } from '../presets';
import { apiService } from '../../services/api';
import { optionsCache } from '../../services/optionsCache';
import {
  OptionContract,
  StrategyRecommendationResponse,
  StrategyRecommendation, StrategyAvailability,
  MarketWeatherContext,
  BrowseContract,
  ExpiryGroupFacts,
  StrategyContract
} from '../../types';
import { useBeginnerProgress } from '../../contexts/BeginnerProgressContext';
import { useFramework } from '../../contexts/FrameworkContext';
import { useStrategy } from '../../contexts/StrategyContext';
import './ContractFilterSidebar.css';

const StockAnalysis: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setStep } = useBeginnerProgress();
  const { activeScoringFramework } = useFramework();
  const { strategy } = useStrategy();

  // Get wizard filters from navigation state (passed from screener)
  const receivedWizardFilters = location.state?.wizardFilters;
  const receivedStrategyType = location.state?.strategyType;
  const receivedBrowseAll = location.state?.browseAll;
  // Roll context from portfolio (when user clicks "See all contracts" from roll modal)
  const receivedRollContext = location.state?.rollContext as {
    positionId: string;
    positionType?: string;  // BAB-31: roll the position's OWN side (CSP → puts)
    ticker: string;
    currentPremium: number;
    currentPrice: number;
    expiryDate: string;
    strikePrice: number;
    metrics: any;
  } | undefined;

  // BAB-32: wheel-turn context — an assigned CSP continuing into a covered call on the
  // assigned shares. Reuses this page's contract picker; confirm posts continue-as-cc.
  const receivedWheelContext = location.state?.wheelContext as {
    positionId: string;
    ticker: string;
    assignmentStrike: number;
    shares: number;
    effectiveCostBasis?: number;
    currentStockPrice?: number;
  } | undefined;

  // BAB-28/31/32: which side to fetch + label.
  // - Wheel turn → always CALLs (you're selling a call on the assigned shares).
  // - Roll → the rolled position's own type (a CSP rolls into puts), NOT the global lens.
  // - Otherwise follow the screener CC/CSP lens.
  const contractType: 'call' | 'put' = receivedWheelContext
    ? 'call'
    : receivedRollContext?.positionType
      ? (receivedRollContext.positionType === 'cash_secured_put' ? 'put' : 'call')
      : (strategy === 'csp' ? 'put' : 'call');

  // Initialize with URL param or empty string to prevent unnecessary API calls
  const initialSymbol = searchParams.get('symbol')?.toUpperCase() || '';
  const [selectedSymbol, setSelectedSymbol] = useState<string>(initialSymbol);
  const [selectedExpiration, setSelectedExpiration] = useState<string>('');
  const [availableExpirations, setAvailableExpirations] = useState<Array<{
    date: string;
    display: string;
    days_to_expiration: number;
    type?: string;
  }>>([]);
  const [isLoadingExpirations, setIsLoadingExpirations] = useState(false);

  // Beginner mode is the only mode (advanced mode removed)
  const isBeginnerMode = true;
  const [beginnerFilters, setBeginnerFilters] = useState<{
    minDays: number;
    maxDays: number;
    minDelta: number;
    maxDelta: number;
    managementStyle: string;
    objective: string;
    isFirstQuestionAnswered: boolean;
    isBothQuestionsAnswered: boolean;
  }>({
    minDays: 0,
    maxDays: 0,
    minDelta: 0,
    maxDelta: 0,
    managementStyle: '',
    objective: '',
    isFirstQuestionAnswered: false,
    isBothQuestionsAnswered: false
  });

  // Store original filter values from wizard for re-activation
  const [originalFilters, setOriginalFilters] = useState<{
    minDays?: number;
    maxDays?: number;
    minDelta?: number;
    maxDelta?: number;
  }>({});
  const [filteredExpirations, setFilteredExpirations] = useState<Array<{
    date: string;
    display: string;
    days_to_expiration: number;
    type?: string;
  }>>([]);
  const [optionsData, setOptionsData] = useState<OptionContract[]>([]);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingBulkOptions, setIsLoadingBulkOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OptionContract | null>(null);

  // Contract comparison state
  const [isComparisonMode, setIsComparisonMode] = useState<boolean>(false);
  const [selectedContractsForComparison, setSelectedContractsForComparison] = useState<OptionContract[]>([]);
  const [showComparisonModal, setShowComparisonModal] = useState<boolean>(false);

  // Market Weather Strategy state
  const [marketWeather, setMarketWeather] = useState<MarketWeatherContext | null>(null);
  const [strategyRecommendations, setStrategyRecommendations] = useState<StrategyRecommendation[]>([]);
  const [strategyAvailability, setStrategyAvailability] = useState<StrategyAvailability | null>(null);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState<boolean>(false);
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [strategyLastUpdated, setStrategyLastUpdated] = useState<string | null>(null);
  // Step 2 is the option chain itself (no separate suggested-contracts view).
  const [showBrowseAll, setShowBrowseAll] = useState<boolean>(true);
  const [browseContracts, setBrowseContracts] = useState<BrowseContract[]>([]);
  const [browseExpiries, setBrowseExpiries] = useState<ExpiryGroupFacts[]>([]);
  const [isLoadingBrowse, setIsLoadingBrowse] = useState<boolean>(false);
  const [selectedStrategyType, setSelectedStrategyType] = useState<string>('recommended');

  // Contract quantity modal state
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [pendingContract, setPendingContract] = useState<{
    strike_price: number;
    premium: number;
    expiration_date: string;
    type: 'strategy' | 'browse' | 'option';
    earnings_inside?: boolean | null;
  } | null>(null);
  const [isAddingToPortfolio, setIsAddingToPortfolio] = useState<boolean>(false);

  // Contract browser filters state
  const [contractFilters, setContractFilters] = useState<ContractFilters>({});
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  // Desktop: the contract filters can be hidden to give the chain the full width (remembered per browser).
  const [contractFiltersHidden, setContractFiltersHidden] = useState<boolean>(() => {
    try { return localStorage.getItem('contractFiltersHidden') === '1'; } catch { return false; }
  });
  const toggleContractFilters = () => setContractFiltersHidden((h) => {
    try { localStorage.setItem('contractFiltersHidden', h ? '0' : '1'); } catch { /* in-memory only */ }
    return !h;
  });

  // Preset state for contract filters
  const [activePresetId, setActivePresetId] = useState<number | null>(null);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);

  // Symbol search for advanced mode
  const fetchingRef = React.useRef<string | null>(null); // Track ongoing fetches

  // Mode transition handler
  const handleModeTransition = useCallback((newIsBeginnerMode: boolean) => {
    // Reset state when switching modes to ensure clean transition
    setSelectedOption(null);

    // In beginner mode, reset filters to force fresh start
    if (newIsBeginnerMode) {
      setBeginnerFilters({
        minDays: 0,
        maxDays: 0,
        minDelta: 0,
        maxDelta: 0,
        managementStyle: '',
        objective: '',
        isFirstQuestionAnswered: false,
        isBothQuestionsAnswered: false
      });
      setSelectedExpiration('');
    } else {
      // In advanced mode, auto-select a good expiration if available
      const goodExpiration = availableExpirations.find(exp =>
        exp.days_to_expiration >= 30 && exp.days_to_expiration <= 45
      );
      if (goodExpiration) {
        setSelectedExpiration(goodExpiration.date);
      } else if (availableExpirations.length > 0) {
        setSelectedExpiration(availableExpirations[0].date);
      }
    }
  }, [availableExpirations]);

  // Beta: beginner mode is hardcoded, no need to listen for changes

  // Update progress step when in beginner mode (always step 5 on Analysis page)
  useEffect(() => {
    if (isBeginnerMode) {
      setStep(5); // On analysis page, user is selecting a contract (step 5)
    }
  }, [isBeginnerMode, setStep]);

  // Automatically apply wizard filters from screener navigation (beginner mode only)
  useEffect(() => {
    if (isBeginnerMode && receivedWizardFilters) {
      console.log('🎯 Auto-applying wizard filters from screener:', receivedWizardFilters);

      // Store original filter values for re-activation
      setOriginalFilters({
        minDays: receivedWizardFilters.minDays || 0,
        maxDays: receivedWizardFilters.maxDays || 0,
        minDelta: receivedWizardFilters.minDelta || 0,
        maxDelta: receivedWizardFilters.maxDelta || 0
      });

      // Apply filters
      setBeginnerFilters({
        minDays: receivedWizardFilters.minDays || 0,
        maxDays: receivedWizardFilters.maxDays || 0,
        minDelta: receivedWizardFilters.minDelta || 0,
        maxDelta: receivedWizardFilters.maxDelta || 0,
        managementStyle: receivedStrategyType || '',
        objective: receivedStrategyType || '',
        isFirstQuestionAnswered: true,
        isBothQuestionsAnswered: true
      });
    }
  }, [isBeginnerMode, receivedWizardFilters, receivedStrategyType]);

  // Step 2 opens on the chain: load all contracts for the symbol and side.
  useEffect(() => {
    if (selectedSymbol) handleBrowseAllContracts();
  }, [selectedSymbol, contractType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatically show browse all view when navigated with browseAll state (e.g., from roll modal)
  useEffect(() => {
    if (receivedBrowseAll && selectedSymbol) {
      setShowBrowseAll(true);
    }
  }, [receivedBrowseAll, selectedSymbol]);

  // Redirect to screening page if in beginner mode and no symbol selected
  useEffect(() => {
    if (isBeginnerMode && !selectedSymbol) {
      console.log('🔄 Redirecting to screening page: beginner mode with no symbol');
      navigate('/screening');
    }
  }, [isBeginnerMode, selectedSymbol, navigate]);

  // Update symbol when URL parameters change
  useEffect(() => {
    const symbolFromUrl = searchParams.get('symbol');
    if (symbolFromUrl) {
      const newSymbol = symbolFromUrl.toUpperCase();
      // Only update if different from current symbol
      if (newSymbol !== selectedSymbol) {
        console.log('🔄 StockAnalysis: Updating symbol to:', newSymbol);
        setSelectedSymbol(newSymbol);
      }
    }
  }, [searchParams, selectedSymbol]);

  // Fetch strategy recommendations when symbol changes (beginner mode)
  useEffect(() => {
    const fetchStrategyRecommendations = async () => {
      if (!selectedSymbol || !isBeginnerMode) return;

      setIsLoadingStrategies(true);
      setStrategyError(null);
      setStrategyLastUpdated(null);
      setStrategyAvailability(null);

      try {
        console.log('🌤️ Fetching strategy recommendations for:', selectedSymbol, contractType);
        const response = await apiService.getStrategyRecommendations(selectedSymbol, 'moderate', contractType);

        if (cancelled) return;

        if (response.status === 'error') {
          setStrategyError(response.message || 'Failed to load recommendations');
          setMarketWeather(null);
          setStrategyRecommendations([]);
          setStrategyAvailability(null);
          setStrategyLastUpdated(response.last_updated || null);
        } else {
          setMarketWeather(response.market_weather);
          setStrategyRecommendations(response.strategies);
          setStrategyAvailability(response.availability ?? null);
          setStrategyLastUpdated(response.last_updated || null);
          if (response.market_weather?.current_price) {
            setCurrentPrice(response.market_weather.current_price);
          }
          console.log('✅ Strategy recommendations loaded:', {
            zone: response.market_weather?.zone,
            strategies: response.strategies.length
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch strategy recommendations:', error);
        setStrategyError('Unable to load strategy recommendations');
        setMarketWeather(null);
        setStrategyRecommendations([]);
        setStrategyAvailability(null);
      } finally {
        if (!cancelled) setIsLoadingStrategies(false);
      }
    };

    let cancelled = false;
    fetchStrategyRecommendations();
    return () => { cancelled = true; };
  }, [selectedSymbol, isBeginnerMode, contractType]);

  // Fetch browse all contracts
  const handleBrowseAllContracts = useCallback(async () => {
    if (!selectedSymbol) return;

    setShowBrowseAll(true);
    setIsLoadingBrowse(true);

    try {
      console.log('📋 Fetching all contracts for:', selectedSymbol, contractType);
      const response = await apiService.browseAllContracts(selectedSymbol, contractType);

      if (response.status === 'success') {
        setBrowseContracts(response.contracts);
        setBrowseExpiries(response.expiries ?? []);
        console.log('✅ Browse contracts loaded:', response.total_contracts);
      } else {
        console.error('Failed to load browse contracts');
        setBrowseContracts([]);
        setBrowseExpiries([]);
      }
    } catch (error) {
      console.error('Failed to fetch browse contracts:', error);
      setBrowseContracts([]);
      setBrowseExpiries([]);
    } finally {
      setIsLoadingBrowse(false);
    }
  }, [selectedSymbol, contractType]);

  // Handle adding strategy contract to portfolio - opens modal for contract quantity
  const handleAddStrategyToPortfolio = useCallback((contract: StrategyContract) => {
    if (!currentPrice || currentPrice <= 0) {
      alert('Current stock price not available. Please wait for data to load.');
      return;
    }

    if (contract.premium == null) {
      alert('This contract has no price yet.');
      return;
    }
    setPendingContract({
      strike_price: contract.strike_price,
      premium: contract.premium,
      expiration_date: contract.expiration_date,
      type: 'strategy',
      earnings_inside: contract.earnings_inside ?? null,
    });
    setShowContractModal(true);
  }, [currentPrice]);

  // Handle adding browse contract to portfolio - opens modal for contract quantity
  const handleAddBrowseToPortfolio = useCallback((contract: BrowseContract) => {
    if (!currentPrice || currentPrice <= 0) {
      alert('Current stock price not available. Please wait for data to load.');
      return;
    }

    if (contract.premium == null) {
      alert('This contract has no price yet.');
      return;
    }
    setPendingContract({
      strike_price: contract.strike_price,
      premium: contract.premium,
      expiration_date: contract.expiration_date,
      type: 'browse',
      earnings_inside: contract.earnings_inside ?? null,
    });
    setShowContractModal(true);
  }, [currentPrice]);


  // Load available expirations and bulk options when symbol changes (OPTIMIZED: Single API call)
  useEffect(() => {
    const fetchDataForSymbol = async () => {
      if (!selectedSymbol) return;

      // Prevent duplicate concurrent requests for the same symbol
      if (fetchingRef.current === selectedSymbol) {
        console.log(`⏭️ Skipping duplicate fetch for ${selectedSymbol}`);
        return;
      }

      console.log('📅 StockAnalysis: Fetching bulk data for symbol:', selectedSymbol);
      fetchingRef.current = selectedSymbol;
      setIsLoadingExpirations(true);
      setIsLoadingBulkOptions(true);

      try {
        // ✅ OPTIMIZED: Single API call instead of 2-3+ calls
        const bulkResponse = await apiService.getBulkOptions(selectedSymbol, 4, 90, 0.1, 0.5, contractType);

        if (cancelled) return;

        console.log(`✅ Bulk data loaded: ${bulkResponse.total_contracts} contracts, ${bulkResponse.expirations?.length || 0} expirations`);

        // Transform expirations to match expected format (add display field)
        const transformedExpirations = (bulkResponse.expirations || []).map(exp => ({
          ...exp,
          display: `${exp.date} (${exp.days_to_expiration} days)`
        }));

        setAvailableExpirations(transformedExpirations);

      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch bulk data:', error);
        setAvailableExpirations([]);
      } finally {
        if (!cancelled) {
          fetchingRef.current = null;
          setIsLoadingExpirations(false);
          setIsLoadingBulkOptions(false);
        }
      }
    };

    let cancelled = false;
    fetchDataForSymbol();
    return () => { cancelled = true; };
  }, [selectedSymbol, isBeginnerMode, contractType]);


  // Filter expirations based on beginner filters or use cache for advanced mode
  useEffect(() => {
    console.log('🔄 Filtering expirations:', {
      isBeginnerMode,
      isFirstQuestionAnswered: beginnerFilters.isFirstQuestionAnswered,
      availableExpirations: availableExpirations.length,
      selectedExpiration,
      cacheHit: optionsCache.has(selectedSymbol)
    });

    if (isBeginnerMode) {
      // Beginner mode: filter by questions answered
      if (!beginnerFilters.isFirstQuestionAnswered) {
        setFilteredExpirations([]);
        return;
      }

      // Check if DTE filter is active (both values > 0)
      const hasDteFilter = beginnerFilters.minDays > 0 && beginnerFilters.maxDays > 0;

      const filtered = availableExpirations.filter(exp => {
        // Always exclude expired/today options
        if (exp.days_to_expiration <= 1) return false;

        // If DTE filter is cleared (removed), show all expirations
        if (!hasDteFilter) return true;

        // Apply DTE filter if active
        return exp.days_to_expiration >= beginnerFilters.minDays &&
               exp.days_to_expiration <= beginnerFilters.maxDays;
      }); // Show all expirations from backend filter

      console.log('📅 Filtered expirations (beginner):', {
        total: availableExpirations.length,
        filtered: filtered.length,
        dayRange: [beginnerFilters.minDays, beginnerFilters.maxDays],
        filteredExps: filtered.map(e => ({ date: e.date, days: e.days_to_expiration }))
      });

      setFilteredExpirations(filtered);

      // Check if current selection is valid in the new filtered list
      const currentSelectionValid = selectedExpiration &&
                                    filtered.some(exp => exp.date === selectedExpiration);

      // Auto-select the first filtered expiration when:
      // 1. First question is answered
      // 2. No expiration selected OR current selection is not in filtered list
      if (filtered.length > 0 && beginnerFilters.isFirstQuestionAnswered &&
          (!selectedExpiration || !currentSelectionValid)) {
        console.log('🎯 Auto-selecting expiration:', filtered[0].date, 'Days:', filtered[0].days_to_expiration);
        console.log('Previous selection:', selectedExpiration, 'was', currentSelectionValid ? 'valid' : 'invalid/empty');
        setSelectedExpiration(filtered[0].date);
      } else if (filtered.length === 0) {
        console.log('⚠️ No valid expirations found for filter range:', [beginnerFilters.minDays, beginnerFilters.maxDays]);
        console.log('Available expirations:', availableExpirations.map(e => ({ date: e.date, days: e.days_to_expiration })));
      }
    } else {
      // Advanced mode: use cache if available for better performance
      if (optionsCache.has(selectedSymbol)) {
        const cachedExpirations = optionsCache.getFilteredExpirations(selectedSymbol, 4, 90);
        setFilteredExpirations(cachedExpirations.map(exp => ({
          date: exp.date,
          display: exp.date, // Simplified for now
          days_to_expiration: exp.daysToExpiration
        })));
        console.log('📦 Using cached expirations for advanced mode:', cachedExpirations.length);
      }
    }
  }, [availableExpirations, beginnerFilters, isBeginnerMode, selectedExpiration, selectedSymbol]);

  // Fetch options chain when expiration is selected (both beginner and advanced modes)
  useEffect(() => {
    const fetchOptions = async () => {
      if (!selectedSymbol || !selectedExpiration) return;

      console.log('📊 Options fetch check:', {
        isBeginnerMode,
        selectedSymbol,
        selectedExpiration,
        isFirstQuestionAnswered: isBeginnerMode ? beginnerFilters.isFirstQuestionAnswered : true,
        cacheAvailable: optionsCache.has(selectedSymbol)
      });

      // Skip fetching in beginner mode if first question not answered
      if (isBeginnerMode && !beginnerFilters.isFirstQuestionAnswered) return;

      setIsLoadingOptions(true);
      try {
        // Use cache-first approach for both modes
        const response = await apiService.getOptionsCached(selectedSymbol, selectedExpiration, true);

        let filteredOptions = response.options || [];

        if (isBeginnerMode) {
          // Beginner mode: apply delta filtering based on second question
          filteredOptions = filteredOptions.filter(opt => {
            // If both questions aren't answered yet, show all options
            if (!beginnerFilters.isBothQuestionsAnswered) {
              return true;
            }

            // If delta is missing, still include the option
            if (opt.delta === undefined || opt.delta === null || opt.delta === 0) {
              return true;
            }

            // For call options, delta should be positive
            const absDelta = Math.abs(opt.delta);

            // Check if delta filter is active (both values > 0)
            const hasDeltaFilter = beginnerFilters.minDelta > 0 && beginnerFilters.maxDelta > 0;

            // If delta filter is cleared (removed), show all options
            if (!hasDeltaFilter) return true;

            // Apply delta filter if active
            return absDelta >= beginnerFilters.minDelta &&
                   absDelta <= beginnerFilters.maxDelta;
          });
        }

        console.log('Options filtering debug:', {
          mode: isBeginnerMode ? 'beginner' : 'advanced',
          source: response.source || 'api',
          totalOptions: response.options?.length || 0,
          filteredCount: filteredOptions.length,
          deltaRange: isBeginnerMode ? [beginnerFilters.minDelta, beginnerFilters.maxDelta] : 'none',
          isBothQuestionsAnswered: isBeginnerMode ? beginnerFilters.isBothQuestionsAnswered : 'n/a'
        });

        if (cancelled) return;
        setOptionsData(filteredOptions);
        if (response.current_price != null) setCurrentPrice(response.current_price);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch options:', error);
        setOptionsData([]);
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    };

    let cancelled = false;
    fetchOptions();
    return () => { cancelled = true; };
  }, [
    selectedSymbol,
    selectedExpiration,
    isBeginnerMode,
    beginnerFilters.isFirstQuestionAnswered,
    beginnerFilters.isBothQuestionsAnswered,
    beginnerFilters.minDelta,
    beginnerFilters.maxDelta
  ]);

  const handleBeginnerFiltersChange = useCallback((filters: typeof beginnerFilters) => {
    // Check if management style changed (but both are answered)
    const managementStyleChanged = beginnerFilters.isFirstQuestionAnswered &&
                                   filters.isFirstQuestionAnswered &&
                                   beginnerFilters.managementStyle !== filters.managementStyle;

    // Check if clearing filters (going from answered to unanswered)
    const clearingFilters = beginnerFilters.isFirstQuestionAnswered && !filters.isFirstQuestionAnswered;

    setBeginnerFilters(filters);

    // Clear stale data when management style changes or filters are cleared
    if (managementStyleChanged || clearingFilters) {
      setSelectedExpiration('');
      setSelectedOption(null);
      setOptionsData([]); // Clear stale options cards
      console.log('🔄 Clearing stale data due to filter change:', { managementStyleChanged, clearingFilters });
    }
  }, [beginnerFilters]);

  // Filter toggle handlers (for beginner mode filter pills)
  const handleToggleDteFilter = useCallback(() => {
    setBeginnerFilters(prev => {
      // Check if currently active
      const isActive = prev.minDays > 0 && prev.maxDays > 0;

      if (isActive) {
        // Deactivate: set to 0
        console.log('🔴 Deactivating DTE filter');
        return {
          ...prev,
          minDays: 0,
          maxDays: 0
        };
      } else {
        // Reactivate: restore original values
        console.log('🟢 Reactivating DTE filter with original values:', {
          minDays: originalFilters.minDays,
          maxDays: originalFilters.maxDays
        });
        return {
          ...prev,
          minDays: originalFilters.minDays || 0,
          maxDays: originalFilters.maxDays || 0
        };
      }
    });
  }, [originalFilters.minDays, originalFilters.maxDays]);

  const handleToggleDeltaFilter = useCallback(() => {
    setBeginnerFilters(prev => {
      // Check if currently active
      const isActive = prev.minDelta > 0 && prev.maxDelta > 0;

      if (isActive) {
        // Deactivate: set to 0
        console.log('🔴 Deactivating Delta filter');
        return {
          ...prev,
          minDelta: 0,
          maxDelta: 0
        };
      } else {
        // Reactivate: restore original values
        console.log('🟢 Reactivating Delta filter with original values:', {
          minDelta: originalFilters.minDelta,
          maxDelta: originalFilters.maxDelta
        });
        return {
          ...prev,
          minDelta: originalFilters.minDelta || 0,
          maxDelta: originalFilters.maxDelta || 0
        };
      }
    });
  }, [originalFilters.minDelta, originalFilters.maxDelta]);

  // Contract comparison handlers
  const handleComparisonToggle = useCallback((option: OptionContract) => {
    setSelectedContractsForComparison(prev => {
      const isAlreadySelected = prev.some(contract => contract.strike === option.strike);
      if (isAlreadySelected) {
        return prev.filter(contract => contract.strike !== option.strike);
      } else {
        // Limit to 3 contracts max for better comparison
        if (prev.length >= 3) {
          return [...prev.slice(1), option];
        }
        return [...prev, option];
      }
    });
  }, []);

  const handleToggleComparisonMode = useCallback(() => {
    setIsComparisonMode(prev => {
      if (prev) {
        // Exiting comparison mode - clear selections
        setSelectedContractsForComparison([]);
      }
      return !prev;
    });
  }, []);

  const handleShowComparison = useCallback(() => {
    if (selectedContractsForComparison.length >= 2) {
      setShowComparisonModal(true);
    }
  }, [selectedContractsForComparison.length]);

  const handleCloseComparison = useCallback(() => {
    setShowComparisonModal(false);
  }, []);

  // Handle adding option from advanced options chain - opens modal for contract quantity
  const handleBuyOption = (option: OptionContract) => {
    if (!currentPrice || currentPrice <= 0) {
      alert('Current stock price not available. Please wait for data to load or try refreshing the page.');
      return;
    }

    // Premium = the option mark (last trade); no quote feed for a real mid.
    const midPrice = option.last_price ?? 0;

    setPendingContract({
      strike_price: option.strike,
      premium: Math.round(midPrice * 100) / 100,
      expiration_date: selectedExpiration,
      type: 'option'
    });
    setShowContractModal(true);
  };

  // Compute expiration options for contract filter sidebar
  const contractExpirationOptions = useMemo(() => {
    const display = new Map(browseContracts.map((c) => [c.expiration_date, c.expiration_display]));
    return browseExpiries.map((e) => ({
      date: e.expiration_date,
      display: display.get(e.expiration_date) ?? e.expiration_date,
      days_to_expiration: e.days_to_expiration,
      count: e.count,
      expiry_cycle: e.expiry_cycle,
      earnings_inside: e.earnings_inside,
    }));
  }, [browseContracts, browseExpiries]);

  // Compute expiration labels map for pills
  const expirationLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    contractExpirationOptions.forEach(exp => {
      labels[exp.date] = exp.display;
    });
    return labels;
  }, [contractExpirationOptions]);

  // Compute dynamic ranges for sliders based on actual data (compute first so distributions can use them)
  const contractRanges = useMemo(() => {
    if (browseContracts.length === 0) return undefined;

    const strikeValues = browseContracts
      .map(c => c.strike_price)
      .filter((v): v is number => v != null && !isNaN(v));
    const arocValues = browseContracts
      .map(c => c.aroc)
      .filter((v): v is number => v != null && !isNaN(v));
    const ivValues = browseContracts
      .map(c => c.implied_volatility)
      .filter((v): v is number => v != null && !isNaN(v));
    const spreadValues = browseContracts
      .map(c => c.spread_load)
      .filter((v): v is number => v != null && !isNaN(v));
    const oiValues = browseContracts
      .map(c => c.open_interest)
      .filter((v): v is number => v != null && !isNaN(v));
    const thetaValues = browseContracts
      .map(c => c.theta)
      .filter((v): v is number => v != null && !isNaN(v));
    const gammaValues = browseContracts
      .map(c => c.gamma)
      .filter((v): v is number => v != null && !isNaN(v));
    const vegaValues = browseContracts
      .map(c => c.vega)
      .filter((v): v is number => v != null && !isNaN(v));

    // Round to nice values
    const roundMin = (val: number) => Math.max(0, Math.floor(val / 5) * 5);
    const roundMax = (val: number) => Math.ceil(val / 5) * 5;

    return {
      strike: strikeValues.length > 0
        ? { min: Math.floor(Math.min(...strikeValues)), max: Math.ceil(Math.max(...strikeValues)) }
        : { min: 0, max: 1000 },
      aroc: arocValues.length > 0
        ? { min: roundMin(Math.min(...arocValues)), max: roundMax(Math.max(...arocValues)) }
        : { min: 0, max: 200 },
      iv: ivValues.length > 0
        ? { min: roundMin(Math.min(...ivValues)), max: roundMax(Math.max(...ivValues)) }
        : { min: 0, max: 200 },
      spread: spreadValues.length > 0
        ? { min: Math.max(0, Math.floor(Math.min(...spreadValues))), max: Math.ceil(Math.max(...spreadValues)) }
        : { min: 0, max: 30 },
      open_interest: oiValues.length > 0
        ? { min: 0, max: Math.ceil(Math.max(...oiValues)) }
        : { min: 0, max: 10000 },
      theta: thetaValues.length > 0
        ? { min: Math.floor(Math.min(...thetaValues) * 100) / 100, max: Math.ceil(Math.max(...thetaValues) * 100) / 100 }
        : { min: -1, max: 0 },
      gamma: gammaValues.length > 0
        ? { min: 0, max: Math.ceil(Math.max(...gammaValues) * 1000) / 1000 }
        : { min: 0, max: 0.1 },
      vega: vegaValues.length > 0
        ? { min: 0, max: Math.ceil(Math.max(...vegaValues) * 100) / 100 }
        : { min: 0, max: 1 }
    };
  }, [browseContracts]);

  // Compute distributions for histogram (using dynamic ranges)
  const contractDistributions = useMemo(() => {
    if (browseContracts.length === 0) return undefined;

    // Get dynamic ranges with fallbacks
    const strikeMin = contractRanges?.strike?.min ?? 0;
    const strikeMax = contractRanges?.strike?.max ?? 1000;
    const arocMin = contractRanges?.aroc?.min ?? 0;
    const arocMax = contractRanges?.aroc?.max ?? 200;
    const ivMin = contractRanges?.iv?.min ?? 0;
    const ivMax = contractRanges?.iv?.max ?? 200;
    const spreadMin = contractRanges?.spread?.min ?? 0;
    const spreadMax = contractRanges?.spread?.max ?? 30;

    // Helper to compute distribution buckets
    const computeDistribution = (
      values: number[],
      min: number,
      max: number,
      bucketCount: number
    ) => {
      const bucketSize = (max - min) / bucketCount;
      const buckets = Array.from({ length: bucketCount }, (_, i) => ({
        min: min + i * bucketSize,
        max: i === bucketCount - 1 ? max : min + (i + 1) * bucketSize,
        count: 0,
        percentage: 0
      }));

      values.forEach(val => {
        const bucketIndex = Math.min(
          Math.floor((val - min) / bucketSize),
          bucketCount - 1
        );
        if (bucketIndex >= 0 && bucketIndex < bucketCount) {
          buckets[bucketIndex].count++;
        }
      });

      const maxCount = Math.max(...buckets.map(b => b.count), 1);
      buckets.forEach(b => {
        b.percentage = (b.count / maxCount) * 100;
      });

      return buckets;
    };

    // Strike price distribution (using dynamic range)
    const strikeValues = browseContracts
      .map(c => c.strike_price)
      .filter((v): v is number => v !== null && v >= strikeMin && v <= strikeMax);
    const strikeDistribution = computeDistribution(strikeValues, strikeMin, strikeMax, 10);

    // Delta distribution (0-0.5 in 10 buckets)
    const deltaValues = browseContracts
      .map(c => c.delta ? Math.abs(c.delta) : null)
      .filter((v): v is number => v !== null && v <= 0.5);
    const deltaDistribution = computeDistribution(deltaValues, 0, 0.5, 10);

    // AROC distribution (using dynamic range)
    const arocValues = browseContracts
      .map(c => c.aroc)
      .filter((v): v is number => v !== null && v >= arocMin && v <= arocMax);
    const arocDistribution = computeDistribution(arocValues, arocMin, arocMax, 10);

    // IV distribution (using dynamic range)
    const ivValues = browseContracts
      .map(c => c.implied_volatility)
      .filter((v): v is number => v !== null && v >= ivMin && v <= ivMax);
    const ivDistribution = computeDistribution(ivValues, ivMin, ivMax, 10);

    // Spread distribution (using dynamic range)
    const spreadValues = browseContracts
      .map(c => c.spread_load)
      .filter((v): v is number => v !== null && v >= spreadMin && v <= spreadMax);
    const spreadDistribution = computeDistribution(spreadValues, spreadMin, spreadMax, 10);

    // Open Interest distribution
    const oiMin = contractRanges?.open_interest?.min ?? 0;
    const oiMax = contractRanges?.open_interest?.max ?? 10000;
    const oiValues = browseContracts
      .map(c => c.open_interest)
      .filter((v): v is number => v !== null && v >= oiMin && v <= oiMax);
    const oiDistribution = computeDistribution(oiValues, oiMin, oiMax, 10);

    // Theta distribution (negative values for calls)
    const thetaMin = contractRanges?.theta?.min ?? -1;
    const thetaMax = contractRanges?.theta?.max ?? 0;
    const thetaValues = browseContracts
      .map(c => c.theta)
      .filter((v): v is number => v !== null && v >= thetaMin && v <= thetaMax);
    const thetaDistribution = computeDistribution(thetaValues, thetaMin, thetaMax, 10);

    // Gamma distribution
    const gammaMin = contractRanges?.gamma?.min ?? 0;
    const gammaMax = contractRanges?.gamma?.max ?? 0.1;
    const gammaValues = browseContracts
      .map(c => c.gamma)
      .filter((v): v is number => v !== null && v >= gammaMin && v <= gammaMax);
    const gammaDistribution = computeDistribution(gammaValues, gammaMin, gammaMax, 10);

    // Vega distribution
    const vegaMin = contractRanges?.vega?.min ?? 0;
    const vegaMax = contractRanges?.vega?.max ?? 1;
    const vegaValues = browseContracts
      .map(c => c.vega)
      .filter((v): v is number => v !== null && v >= vegaMin && v <= vegaMax);
    const vegaDistribution = computeDistribution(vegaValues, vegaMin, vegaMax, 10);

    return {
      strike: strikeDistribution,
      delta: deltaDistribution,
      aroc: arocDistribution,
      iv: ivDistribution,
      spread: spreadDistribution,
      open_interest: oiDistribution,
      theta: thetaDistribution,
      gamma: gammaDistribution,
      vega: vegaDistribution
    };
  }, [browseContracts, contractRanges]);

  // Filter browse contracts based on filters
  const filteredBrowseContracts = useMemo(() => {
    return browseContracts.filter(contract => {
      // Filter by expiration dates
      if (contractFilters.expirations && contractFilters.expirations.length > 0) {
        if (!contractFilters.expirations.includes(contract.expiration_date)) {
          return false;
        }
      }

      // Filter by strike price
      if (contract.strike_price !== null) {
        if (contractFilters.strike_min !== undefined && contract.strike_price < contractFilters.strike_min) {
          return false;
        }
        if (contractFilters.strike_max !== undefined && contract.strike_price > contractFilters.strike_max) {
          return false;
        }
      }

      // Filter by delta
      if (contract.delta !== null) {
        const absDelta = Math.abs(contract.delta);
        if (contractFilters.delta_min !== undefined && absDelta < contractFilters.delta_min) {
          return false;
        }
        if (contractFilters.delta_max !== undefined && absDelta > contractFilters.delta_max) {
          return false;
        }
      }

      // Filter by annualized return
      if (contractFilters.aroc_min !== undefined && (contract.aroc == null || contract.aroc < contractFilters.aroc_min)) {
        return false;
      }
      if (contractFilters.aroc_max !== undefined && (contract.aroc == null || contract.aroc > contractFilters.aroc_max)) {
        return false;
      }

      // Filter by IV
      if (contract.implied_volatility !== null) {
        const iv = contract.implied_volatility;
        if (contractFilters.iv_min !== undefined && iv < contractFilters.iv_min) {
          return false;
        }
        if (contractFilters.iv_max !== undefined && iv > contractFilters.iv_max) {
          return false;
        }
      }

      // Filter by spread
      if (contractFilters.spread_max !== undefined && contract.spread_load != null && contract.spread_load > contractFilters.spread_max) {
        return false;
      }

      // Filter by open interest
      if (contractFilters.open_interest_min !== undefined && (contract.open_interest == null || contract.open_interest < contractFilters.open_interest_min)) {
        return false;
      }

      // Filter by theta
      if (contract.theta !== null) {
        if (contractFilters.theta_min !== undefined && contract.theta < contractFilters.theta_min) {
          return false;
        }
        if (contractFilters.theta_max !== undefined && contract.theta > contractFilters.theta_max) {
          return false;
        }
      }

      // Filter by gamma
      if (contract.gamma !== null) {
        if (contractFilters.gamma_min !== undefined && contract.gamma < contractFilters.gamma_min) {
          return false;
        }
        if (contractFilters.gamma_max !== undefined && contract.gamma > contractFilters.gamma_max) {
          return false;
        }
      }

      // Filter by vega
      if (contract.vega !== null) {
        if (contractFilters.vega_min !== undefined && contract.vega < contractFilters.vega_min) {
          return false;
        }
        if (contractFilters.vega_max !== undefined && contract.vega > contractFilters.vega_max) {
          return false;
        }
      }

      return true;
    });
  }, [browseContracts, contractFilters]);

  // Handle modal confirmation - actually creates the position with num_contracts
  // BAB-71: `intent` is the user's Discipline stance chosen in the modal (default 'hold').
  // Gold v2 phase 3b: the fills the user confirmed in the dialog are what we
  // record (premium, stock, buy-back); the trade date is the server's New York date.
  const handleConfirmAddToPortfolio = async (numContracts: number, intent: 'hold' | 'wheel' | 'release' = 'hold', fills?: ConfirmedFills) => {
    if (!pendingContract) return;
    const premiumFill = fills?.premium ?? pendingContract.premium;
    const stockFill = fills?.stockPrice ?? currentPrice;

    setIsAddingToPortfolio(true);
    try {
      let actionText: string;

      if (receivedWheelContext) {
        // BAB-32: wheel turn — sell a covered call on the shares assigned from the CSP.
        // continue-as-cc creates the CC leg in the same campaign (assignment strike = basis).
        await apiService.continueAsCoveredCall(receivedWheelContext.positionId, {
          newStrikePrice: pendingContract.strike_price,
          newExpiryDate: pendingContract.expiration_date,
          newPremium: premiumFill,
          currentStockPrice: currentPrice,
          notes: `Wheel: covered call on ${receivedWheelContext.shares} shares assigned at $${receivedWheelContext.assignmentStrike}`,
        });
        actionText = `🎡 Wheel continued — sold a $${pendingContract.strike_price} covered call on your ${selectedSymbol} shares!`;
      } else if (receivedRollContext) {
        // Use atomic roll endpoint to preserve campaign/leg history
        console.log('Rolling position via rollPosition():', receivedRollContext.positionId);

        const expDisplay = new Date(pendingContract.expiration_date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });

        await apiService.rollPosition(receivedRollContext.positionId, {
          buybackPremium: fills?.buyback ?? receivedRollContext.currentPremium,
          newExpiryDate: pendingContract.expiration_date,
          newStrikePrice: pendingContract.strike_price,
          newPremium: premiumFill,
          currentStockPrice: stockFill,
          notes: `Rolled to ${expDisplay} $${pendingContract.strike_price} strike`
        });

        actionText = `Successfully rolled ${selectedSymbol} position to $${pendingContract.strike_price} strike!`;
      } else {
        // Normal add-to-portfolio (no roll context)
        const position: any = {
          id: Date.now().toString(),
          ticker: selectedSymbol,
          // entry_date omitted: the server records the New York date (et_today)
          expiry_date: pendingContract.expiration_date,
          strike_price: pendingContract.strike_price,
          entry_premium: premiumFill,
          entry_price: stockFill,
          num_contracts: numContracts,
          // BAB-28: a put picked under the CSP lens saves as a cash-secured put so it
          // gets put-aware PME / P&L (BAB-15/16/17), not a covered call.
          position_type: contractType === 'put' ? 'cash_secured_put' : 'covered_call',
          // BAB-71: persist the user's chosen Discipline intent at open.
          intent,
        };
        // Tag with active scoring framework if one is selected
        if (activeScoringFramework) {
          position.framework_id = activeScoringFramework.id;
        }

        console.log('Creating portfolio position with data:', position);
        await apiService.createPortfolioPosition(position);

        actionText = `Successfully added ${numContracts} contract${numContracts > 1 ? 's' : ''} of ${selectedSymbol} $${pendingContract.strike_price} ${contractType === 'put' ? 'put' : 'call'} to portfolio!`;
      }

      alert(actionText);
      setShowContractModal(false);
      setPendingContract(null);

      // If this was a roll or a wheel turn, navigate back to portfolio
      if (receivedRollContext || receivedWheelContext) {
        navigate('/portfolio');
      }
    } catch (error: any) {
      console.error('Failed to add position to portfolio:', error);
      alert(`Failed to add position: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsAddingToPortfolio(false);
    }
  };

  return (
    <Layout>
      <div className="app-page-container">


        {/* Page head: ticker, side, last */}
        <div className="flow-step">
          <button type="button" className="flow-step-back" onClick={() => navigate('/screening')}>Step 1 · Stock</button>
          <span className="flow-step-sep">›</span>
          <span className="flow-step-n">Step 2</span>
          <span className="flow-step-t">Pick the contract</span>
        </div>
        <header className="an-head">
          <h1 className="an-title">{selectedSymbol}<span>{contractType === 'put' ? 'Puts' : 'Calls'}</span></h1>
          {currentPrice > 0 && <span className="an-px">{currentPrice.toFixed(2)}</span>}
          <span className="an-sp" />
          {showBrowseAll && (
            <button type="button" className="screener-filters-toggle an-filters-toggle" aria-pressed={!contractFiltersHidden} onClick={toggleContractFilters}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
              {contractFiltersHidden ? 'Show filters' : 'Hide filters'}
            </button>
          )}
          <span className="an-count">{filteredBrowseContracts.length} of {browseContracts.length} contracts</span>
        </header>

        {/* Mode banner: rolling or continuing the wheel */}
        {receivedWheelContext && (() => {
          const noCall = !isLoadingBrowse && browseContracts.length === 0;
          const basisStr = receivedWheelContext.effectiveCostBasis != null
            ? `, cost basis ${receivedWheelContext.effectiveCostBasis.toFixed(2)}` : '';
          return (
            <div className={`an-mode ${noCall ? 'is-warn' : ''}`} role="status">
              <b>{noCall ? 'No tradeable call right now' : 'Continuing the wheel'}</b>
              {noCall
                ? <> for {selectedSymbol}: likely illiquid or no market data (15-min delay / after hours). Your {receivedWheelContext.shares} shares (assigned at {receivedWheelContext.assignmentStrike.toFixed(2)}{basisStr}) are unaffected.</>
                : <>: sell a call on your {receivedWheelContext.shares} {selectedSymbol} shares (assigned at {receivedWheelContext.assignmentStrike.toFixed(2)}{basisStr}).</>}
            </div>
          );
        })()}
        {receivedRollContext && (
          <div className="an-mode" role="status"><b>Rolling</b>: pick the contract to roll into.</div>
        )}

        <div className="an-facts-wrap"><MarketWeatherHeader marketWeather={marketWeather} isLoading={isLoadingStrategies} /></div>
        {showBrowseAll ? (
            <div className="beginner-mode-layout-accordion">
              <div className={`screener-layout ${contractFiltersHidden ? 'is-filters-hidden' : ''}`}>
                <ContractFilterSidebar
                  filters={contractFilters}
                  onChange={setContractFilters}
                  resultCount={filteredBrowseContracts.length}
                  isLoadingCount={isLoadingBrowse}
                  expirationOptions={contractExpirationOptions}
                  distributions={contractDistributions}
                  ranges={contractRanges}
                  activePresetId={activePresetId}
                  onSelectPreset={setActivePresetId}
                  onSavePreset={() => setShowSavePresetModal(true)}
                  nextEarningsDate={marketWeather?.next_earnings_date}
                />
                <div className="screener-content an-browse">
                  {isLoadingBrowse ? (
                    <p className="an-empty"><Icon name="spinner" spin size={13} /> Loading contracts…</p>
                  ) : filteredBrowseContracts.length > 0 ? (
                    <ContractLadder
                      rows={filteredBrowseContracts.map(c => ({ key: c.contract_ticker, contract: c }))}
                      price={currentPrice || null}
                      isPut={contractType === 'put'}
                      earningsDate={marketWeather?.next_earnings_date}
                      expiries={browseExpiries}
                      actionLabel={receivedRollContext ? 'Roll to' : receivedWheelContext ? 'Sell call' : 'Add'}
                      onAction={handleAddBrowseToPortfolio}
                      chain
                      compact
                      ariaLabel={`${selectedSymbol} ${contractType} contracts`}
                    />
                  ) : (
                    <div className="an-empty">
                      No contracts match your filters.
                      <button type="button" className="an-link" onClick={() => setContractFilters({})}>Reset filters</button>
                    </div>
                  )}
                </div>
                <button
                  className="filter-fab"
                  onClick={() => setShowMobileFilters(true)}
                  aria-label="Open filters"
                >
                  <AdjustmentsHorizontalIcon className="filter-fab-icon" />
                  {Object.keys(contractFilters).filter(k => {
                    const val = contractFilters[k as keyof ContractFilters];
                    if (Array.isArray(val)) return val.length > 0;
                    return val !== undefined;
                  }).length > 0 && (
                    <span className="filter-fab-badge">
                      {Object.keys(contractFilters).filter(k => {
                        const val = contractFilters[k as keyof ContractFilters];
                        if (Array.isArray(val)) return val.length > 0;
                        return val !== undefined;
                      }).length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          ) : null
        }
      </div>

      {/* Contract Comparison Modal */}
      {showComparisonModal && (
        <ContractComparison
          contracts={selectedContractsForComparison}
          currentPrice={currentPrice}
          daysToExpiration={filteredExpirations.find(e => e.date === selectedExpiration)?.days_to_expiration || 0}
          onClose={handleCloseComparison}
        />
      )}

      {/* Contract Quantity Modal */}
      <ContractQuantityModal
        isOpen={showContractModal}
        onClose={() => {
          if (!isAddingToPortfolio) {
            setShowContractModal(false);
            setPendingContract(null);
          }
        }}
        onConfirm={handleConfirmAddToPortfolio}
        ticker={selectedSymbol}
        strikePrice={pendingContract?.strike_price || 0}
        expirationDate={pendingContract?.expiration_date || ''}
        premium={pendingContract?.premium || 0}
        isLoading={isAddingToPortfolio}
        isRollMode={!!receivedRollContext}
        isWheelMode={!!receivedWheelContext}
        nextEarningsDate={marketWeather?.next_earnings_date}
        earningsInside={pendingContract?.earnings_inside ?? null}
        daysToEarnings={marketWeather?.days_to_earnings ?? null}
        contractType={contractType}
        stockPrice={currentPrice ?? null}
        buybackPrice={receivedRollContext?.currentPremium ?? null}
      />

      {/* Save Preset Modal */}
      <SavePresetModal
        isOpen={showSavePresetModal}
        onClose={() => setShowSavePresetModal(false)}
        contractFilters={{
          dte_min: contractFilters.delta_min ? undefined : undefined,
          dte_max: contractFilters.delta_max ? undefined : undefined,
          delta_min: contractFilters.delta_min,
          delta_max: contractFilters.delta_max,
          aroc_min: contractFilters.aroc_min,
          aroc_max: contractFilters.aroc_max,
          contract_iv_min: contractFilters.iv_min,
          contract_iv_max: contractFilters.iv_max,
          contract_spread_max: contractFilters.spread_max,
          open_interest_min: contractFilters.open_interest_min,
          theta_min: contractFilters.theta_min,
          theta_max: contractFilters.theta_max,
          gamma_min: contractFilters.gamma_min,
          gamma_max: contractFilters.gamma_max,
          vega_min: contractFilters.vega_min,
          vega_max: contractFilters.vega_max,
        }}
        onPresetSaved={(presetId) => {
          setActivePresetId(presetId);
          setShowSavePresetModal(false);
        }}
      />
    </Layout>
  );
};

export default StockAnalysis;